
import React from "react";
import { Menu, Database, Search, Printer, Download, Save, Share2, Upload, Sun, Moon } from "lucide-react";
import { Link } from "wouter";
import { ClientAwareLink } from "../ClientAwareLink";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { SqlHealthProLogo } from "../logos";

interface HeaderProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  selectedClient: any;
  currentClient: string;
  handleLogoClick: () => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  location: any;
  reportsDropdownOpen: boolean;
  setReportsDropdownOpen: (open: boolean) => void;
  reportsDropdownRef: React.RefObject<HTMLDivElement>;
  setUploadModalOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  selectedClient,
  currentClient,
  handleLogoClick,
  searchTerm,
  setSearchTerm,
  location,
  reportsDropdownOpen,
  setReportsDropdownOpen,
  reportsDropdownRef,
  setUploadModalOpen
}) => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Calculate initials
  const getInitials = () => {
    if (!user) return '??';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.firstName) {
      return user.firstName[0].toUpperCase();
    }
    if (user.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  const initials = getInitials();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-shiftleft-800 shadow-lg transition-colors h-[74px] flex items-center">
      <div className="flex-1 flex items-center justify-between px-6">
        {/* Left: Logo and Main Navigation */}
        <div className="flex items-center gap-6">
          <Link href="/" onClick={handleLogoClick}>
            <span className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity">
              <SqlHealthProLogo size={48} className="w-8 h-8 sm:w-12 sm:h-12" />
              <span className="text-lg sm:text-xl font-bold text-white hidden sm:block">ShiftLeft</span>
              {selectedClient && (selectedClient.logoBase64 || selectedClient.logoUrl) && (
                <img
                  src={selectedClient.logoBase64 || selectedClient.logoUrl}
                  alt={selectedClient.name}
                  className="w-8 h-8 object-contain rounded"
                />
              )}
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {[
              { href: "/design", label: "Design" },
              { href: "/plan", label: "Plan" },
              { href: "/code", label: "Code" },
              { href: "/build", label: "Build" },
              { href: "/stabilize", label: "Stabilize" },
              { href: "/operate", label: "Operate" },
              { href: "/observe", label: "Observe" },
            ].map((link) => (
              <ClientAwareLink key={link.href} href={link.href}>
                <span className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors cursor-pointer ${location.startsWith(link.href)
                  ? "bg-shiftleft-600 text-white"
                  : "text-shiftleft-200 hover:text-white hover:bg-shiftleft-700"
                  }`}>
                  {link.label}
                </span>
              </ClientAwareLink>
            ))}
          </nav>
        </div>

        {/* Right: Search, Theme Toggle, and Actions */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="relative hidden lg:block">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-shiftleft-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search..."
              className="pl-9 pr-4 py-2 w-48 xl:w-72 bg-shiftleft-700 border border-shiftleft-600 focus:bg-shiftleft-600 focus:border-shiftleft-500 rounded-lg text-sm text-white placeholder-shiftleft-400 focus:outline-none focus:ring-2 focus:ring-shiftleft-500 transition-all"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Mobile Search Icon */}
          <button className="lg:hidden p-2 rounded-lg hover:bg-shiftleft-700 text-shiftleft-200 hover:text-white transition-colors">
            <Search className="w-5 h-5" />
          </button>

          {/* Theme Switcher */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg hover:bg-shiftleft-700 text-shiftleft-200 hover:text-white transition-colors"
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          <div className="hidden md:flex items-center gap-1 border-x border-shiftleft-600 px-4 mx-2">
            {[
              { icon: Printer, label: "Print" },
              { icon: Download, label: "Export" },
              { icon: Share2, label: "Share" },
              { icon: Upload, label: "Upload", onClick: () => setUploadModalOpen(true) },
            ].map((btn) => (
              <button
                key={btn.label}
                onClick={btn.onClick}
                className="p-2 rounded-md hover:bg-shiftleft-700 text-shiftleft-300 hover:text-white transition-colors"
                title={btn.label}
              >
                <btn.icon className="w-[18px] h-[18px]" />
              </button>
            ))}
          </div>
        </div>

        {/* User Profile / Status */}
        <div className="flex items-center gap-3 pl-2">
          <div className="flex flex-col items-end hidden sm:flex">
            <span className="text-[13px] font-semibold text-white leading-none mb-1">
              {user?.firstName || 'User'}
            </span>
            <span className="text-[11px] text-shiftleft-300 leading-none">
              {currentClient || 'Global Account'}
            </span>
          </div>
          <div className="relative group cursor-pointer">
            <div className="w-9 h-9 ring-2 ring-shiftleft-600 bg-shiftleft-500 rounded-full flex items-center justify-center text-white font-bold overflow-hidden shadow-sm">
              {initials}
            </div>
          </div>
        </div>
      </div>
    </header >
  );
};

export default Header;
